import React from 'react';
import { Link } from 'react-router-dom';
import { useLandingPage } from '@/presentation/landing/useLandingPage';
import { useAppTranslation } from '@/i18n/useAppTranslation';
import { LocaleSelect } from '@/presentation/dashboard/LocaleSelect';

function Hero({ hero, onStart, onLogin, onGuest, t }) {
  return (
    <section id="home" className="relative overflow-hidden px-6 pb-24 pt-16">
      <div className="pointer-events-none absolute inset-x-0 top-0 -z-10 h-full bg-gradient-to-b from-cyan-50 via-emerald-50/60 to-white" />
      <div className="mx-auto grid max-w-7xl items-center gap-12 lg:grid-cols-2">
        <div className="space-y-7">
          <p className="inline-flex items-center gap-2 rounded-full bg-amber-100 px-4 py-2 text-sm font-semibold text-amber-700">
            <span className="material-symbols-outlined !text-base">auto_awesome</span>
            {t('landing.hero.badge')}
          </p>
          <h1 className="font-headline text-5xl font-extrabold leading-tight text-slate-900 md:text-6xl">
            {t('landing.hero.title')}
          </h1>
          <p className="max-w-xl text-lg leading-relaxed text-slate-600">{t('landing.hero.subtitle')}</p>
          <div className="flex flex-wrap gap-4">
            <button
              onClick={onStart}
              className="rounded-2xl bg-emerald-700 px-8 py-4 text-lg font-bold text-white shadow-lg shadow-emerald-200 transition hover:scale-[1.02]"
            >
              {t('landing.hero.primaryCta')}
            </button>
            <button
              onClick={onLogin}
              className="rounded-2xl border border-slate-300 bg-white px-8 py-4 text-lg font-bold text-slate-700 transition hover:bg-slate-50"
            >
              {t('landing.hero.secondaryCta')}
            </button>
            <button
              onClick={onGuest}
              className="rounded-2xl border border-emerald-300 bg-emerald-50 px-8 py-4 text-lg font-bold text-emerald-700 transition hover:bg-emerald-100"
            >
              {t('landing.hero.trialCta')}
            </button>
          </div>
        </div>
        <div className="relative">
          <div className="absolute inset-0 -rotate-6 rounded-[2.5rem] bg-emerald-200/50 blur-3xl" />
          <div className="relative rotate-2 rounded-[2rem] border border-white bg-white p-6 shadow-2xl transition hover:rotate-0">
            <img
              className="aspect-[4/3] w-full rounded-[1.5rem] object-cover"
              src={hero.imageUrl}
              alt={t('landing.heroImageAlt')}
            />
            <div className="absolute -bottom-6 -left-6 rounded-2xl bg-white p-4 shadow-xl">
              <p className="text-sm font-bold text-slate-900">{t('landing.heroCardTitle')}</p>
              <p className="text-xs text-slate-500">{t('landing.heroCardSubtitle')}</p>
            </div>
            <div className="absolute -right-5 -top-5 flex h-20 w-20 rotate-12 flex-col items-center justify-center rounded-full bg-rose-400 text-xs font-bold text-white">
              <span className="text-xl">95%</span>
              {t('landing.heroBadgeEffect')}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

function Features({ features, t }) {
  return (
    <section id="features" className="bg-sky-50 px-6 py-24">
      <div className="mx-auto max-w-7xl">
        <div className="mb-12 text-center">
          <h2 className="font-headline text-4xl font-bold text-slate-900">{t('landing.featuresTitle')}</h2>
          <p className="mx-auto mt-3 max-w-2xl text-lg text-slate-600">
            {t('landing.featuresSubtitle')}
          </p>
        </div>
        <div className="grid gap-6 md:grid-cols-3">
          {features.map((feature) => (
            <article
              key={feature.id}
              className="rounded-[2rem] border border-white bg-white p-8 shadow-sm transition hover:-translate-y-1 hover:shadow-xl"
            >
              <div className="mb-6 flex h-14 w-14 items-center justify-center rounded-2xl bg-emerald-100 text-2xl text-emerald-700">
                <span className="material-symbols-outlined !text-3xl">{feature.icon}</span>
              </div>
              <h3 className="font-headline text-2xl font-bold text-slate-900">{t(`landing.features.${feature.id}.title`)}</h3>
              <p className="mt-4 leading-relaxed text-slate-600">{t(`landing.features.${feature.id}.desc`)}</p>
            </article>
          ))}
        </div>
      </div>
    </section>
  );
}

function AudienceCards({ audiences, t }) {
  return (
    <section id="audiences" className="px-6 py-24">
      <div className="mx-auto max-w-7xl">
        <div className="mb-12 flex flex-col gap-4 md:flex-row md:items-end md:justify-between">
          <div>
            <h2 className="font-headline text-4xl font-bold text-slate-900">{t('landing.audiencesTitle')}</h2>
            <p className="mt-2 text-lg text-slate-600">{t('landing.audiencesSubtitle')}</p>
          </div>
          <div className="flex gap-2">
            <span className="h-3 w-3 rounded-full bg-emerald-300" />
            <span className="h-3 w-3 rounded-full bg-amber-300" />
            <span className="h-3 w-3 rounded-full bg-rose-300" />
          </div>
        </div>

        <div className="grid gap-5 sm:grid-cols-2 xl:grid-cols-5">
          {audiences.map((item) => (
            <article
              key={item.tag}
              className="rounded-[1.5rem] border border-slate-100 bg-white p-6 shadow-sm transition hover:-translate-y-1 hover:shadow-lg"
            >
              <img
                className="mb-5 aspect-square w-full rounded-xl object-cover"
                src={item.imageUrl}
                alt={t(`landing.audiences.${String(item.tag || '').toLowerCase()}.role`)}
              />
              <h3 className="font-headline text-lg font-bold text-slate-900">
                {t(`landing.audiences.${String(item.tag || '').toLowerCase()}.role`)}
              </h3>
              <p className="mt-2 text-sm leading-relaxed text-slate-600">
                {t(`landing.audiences.${String(item.tag || '').toLowerCase()}.summary`)}
              </p>
              <span className="mt-4 inline-block rounded-full bg-slate-100 px-3 py-1 text-xs font-bold uppercase tracking-wide text-slate-700">
                {item.tag}
              </span>
            </article>
          ))}
        </div>
      </div>
    </section>
  );
}

function Footer({ footer, t }) {
  return (
    <footer className="bg-slate-100 px-6 pb-8 pt-16">
      <div className="mx-auto max-w-7xl">
        <div className="grid gap-10 md:grid-cols-4">
          <div>
            <p className="font-headline text-2xl font-extrabold text-cyan-500">QuizAtelier</p>
            <p className="mt-3 text-slate-600">{t('landing.footerTagline')}</p>
          </div>
          <FooterLinks title={t('landing.footerProduct')} items={footer.productLinks} t={t} prefix="product" />
          <FooterLinks title={t('landing.footerSupport')} items={footer.supportLinks} t={t} prefix="support" />
          <FooterLinks title={t('landing.footerAbout')} items={footer.aboutLinks} t={t} prefix="about" />
        </div>
        <div className="mt-10 border-t border-slate-200 pt-6 text-sm text-slate-500">
          {t('landing.footerCopyright')}
        </div>
      </div>
    </footer>
  );
}

function FooterLinks({ title, items, t, prefix }) {
  return (
    <div>
      <h4 className="font-headline text-lg font-bold text-slate-900">{title}</h4>
      <ul className="mt-4 space-y-2 text-slate-600">
        {items.map((item, idx) => (
          <li key={`${prefix}-${idx}`}>{t(`landing.footerLinks.${prefix}.${idx}`)}</li>
        ))}
      </ul>
    </div>
  );
}

export default function LandingPage({ onStart, onLogin, onGuest }) {
  const { t, locale, setLocale } = useAppTranslation();
  const { content } = useLandingPage();

  return (
    <main className="bg-white text-slate-900">
      <header className="sticky top-0 z-40 border-b border-cyan-100 bg-white/80 backdrop-blur">
        <div className="mx-auto flex w-full max-w-7xl items-center justify-between px-6 py-3">
          <div className="flex items-center gap-8">
            <span className="font-headline text-2xl font-extrabold text-cyan-500">QuizAtelier</span>
            <nav className="hidden items-center gap-5 md:flex">
              <a className="font-semibold text-cyan-600" href="#home">{t('landing.navHome')}</a>
              <a className="text-slate-600 hover:text-cyan-600" href="#features">{t('landing.navFeatures')}</a>
              <a className="text-slate-600 hover:text-cyan-600" href="#audiences">{t('landing.navLibrary')}</a>
              <Link className="text-slate-600 hover:text-cyan-600" to="/help/quiz-google-sheet">
                {t('landing.navSheetToQuiz')}
              </Link>
            </nav>
          </div>
          <div className="flex items-center gap-2">
            <LocaleSelect value={locale} onChange={setLocale} ariaLabel={t('auth.langSwitch')} />
            <button
              onClick={onLogin}
              className="rounded-xl border border-slate-200 px-4 py-2 text-sm font-semibold text-slate-700 hover:bg-slate-50"
            >
              {t('landing.login')}
            </button>
          </div>
        </div>
      </header>
      <Hero hero={content.hero} onStart={onStart} onLogin={onLogin} onGuest={onGuest} t={t} />
      <Features features={content.features} t={t} />
      <AudienceCards audiences={content.audiences} t={t} />
      <section className="bg-emerald-700 px-6 py-20 text-white">
        <div className="mx-auto max-w-5xl rounded-[2.5rem] bg-white/10 p-10 text-center backdrop-blur">
          <h2 className="font-headline text-4xl font-extrabold">{t('landing.ctaTitle')}</h2>
          <p className="mx-auto mt-4 max-w-2xl text-white/80">
            {t('landing.ctaSubtitle')}
          </p>
          <div className="mt-8 flex flex-wrap justify-center gap-4">
            <button
              onClick={onStart}
              className="rounded-2xl bg-white px-8 py-4 text-lg font-bold text-emerald-700"
            >
              {t('landing.ctaPrimary')}
            </button>
            <button
              onClick={onGuest}
              className="rounded-2xl border border-white/50 px-8 py-4 text-lg font-bold text-white"
            >
              {t('landing.ctaSecondary')}
            </button>
          </div>
        </div>
      </section>
      <Footer footer={content.footer} t={t} />
    </main>
  );
}

